import { completeOnboarding, skipOnboarding } from "@/features/organizations/onboarding.api";

/** How the Admin left the full-page onboarding flow. */
export type OnboardingOutcome = "completed" | "skipped";

/**
 * Ends the Admin full-page onboarding flow on the server.
 *
 * ## Why the outcome matters (ONBOARDING-SKIP-AND-BREADCRUMB-1)
 *
 * The server does NOT read the stored `completed` flag — `getOnboardingStatus`
 * computes `completed = workspaceCount > 0`. The only settings field it reads
 * back is `skipped`. So for a user who leaves at the workspace step WITHOUT
 * creating one, `skipped` is the single thing that can end the flow.
 *
 * The previous implementation always called `complete` first and only fell back
 * to `skip` if that threw. `complete` never throws, so `skip` never ran — and
 * `complete` writes `skipped: false`. That left the skipping user at
 * `completed=false, skipped=false` ⇒ `mustOnboard=true` ⇒ `not_started` ⇒
 * DashboardLayout GATE 1 bounced them straight back into the wizard. Skip was a
 * loop whose only exit was creating a workspace.
 *
 * There is deliberately no cross-fallback: answering a failed `skip` with
 * `complete` would write `skipped: false` and re-arm the exact retrap.
 */
export async function finalizeAdminOnboardingOnServer(
  outcome: OnboardingOutcome = "completed",
): Promise<boolean> {
  try {
    if (outcome === "skipped") {
      await skipOnboarding();
    } else {
      await completeOnboarding();
    }
    return true;
  } catch {
    return false;
  }
}
